import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { isLowPowerDevice, systemPrefersReducedMotion } from "../lib/fx";
import { local, uid } from "../lib/storage";
import { sfx } from "../lib/sound";
import { unique } from "../engine/random";
import type { DecisionEntry, DecisionItem, Prefs, WheelList } from "../types";
import { useAuth } from "./AuthContext";

interface DataValue {
  history: DecisionItem[];
  favourites: DecisionItem[];
  prefs: Prefs;
  fastMode: boolean;
  signedIn: boolean;
  addHistory: (e: DecisionEntry) => void;
  clearHistory: () => void;
  isFavourite: (e: DecisionEntry) => boolean;
  toggleFavourite: (e: DecisionEntry) => boolean;
  removeFavourite: (id: string) => void;
  setPrefs: (p: Partial<Prefs>) => void;
  recordDislike: (label: string) => void;
  lists: WheelList[];
  saveList: (l: WheelList) => void;
  deleteList: (id: string) => void;
}

const DataCtx = createContext<DataValue>(null as any);
const sameKey = (a: DecisionEntry, b: DecisionEntry) => a.kind === b.kind && a.title === b.title && a.result === b.result;
const toIn = (i: DecisionItem) => ({ client_id: i.id, kind: i.kind, title: i.title.slice(0, 120), result: i.result.slice(0, 200), category: i.category || null, created_at: i.created_at });
const listIn = (l: WheelList) => ({ client_id: l.id, name: l.name, options: l.options, weights: l.weights || {}, updated_at: l.updated_at || undefined });
const newItem = (e: DecisionEntry): DecisionItem => ({ ...e, id: uid(), created_at: new Date().toISOString() });

export function DataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const signedIn = !!user;
  const [history, setHistory] = useState<DecisionItem[]>(local.history());
  const [favourites, setFavourites] = useState<DecisionItem[]>(local.favourites());
  const [prefs, setPrefsState] = useState<Prefs>(local.prefs());
  const [lists, setLists] = useState<WheelList[]>(local.lists());

  useEffect(() => {
    if (user === null) return;
    if (!user) {
      setHistory(local.history());
      setFavourites(local.favourites());
      setLists(local.lists());
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/me/sync", { history: local.history().map(toIn), favourites: local.favourites().map(toIn), lists: local.lists().map(listIn) });
        setHistory(data.history);
        setFavourites(data.favourites);
        setLists(data.lists);
        local.setHistory([]);
        local.setFavourites([]);
        local.setLists([]);
        const remote = (await api.get("/me/preferences")).data as Prefs;
        const lp = local.prefs();
        const merged: Prefs = {
          ...remote,
          reduced_motion: remote.reduced_motion || lp.reduced_motion,
          sound_effects: remote.sound_effects || lp.sound_effects,
          favourite_categories: unique([...remote.favourite_categories, ...lp.favourite_categories]),
          dislikes: unique([...remote.dislikes, ...lp.dislikes]).slice(-200),
        };
        setPrefsState(merged);
        local.setPrefs(merged);
        api.put("/me/preferences", merged).catch(() => undefined);
      } catch {
        /* keep local data if sync fails */
      }
    })();
  }, [user]);

  const addHistory = useCallback((e: DecisionEntry) => {
    const item = newItem(e);
    setHistory((prev) => {
      const next = [item, ...prev].slice(0, 200);
      if (!signedIn) local.setHistory(next);
      return next;
    });
    if (signedIn) api.post("/me/history", toIn(item)).catch(() => undefined);
  }, [signedIn]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    if (signedIn) api.delete("/me/history").catch(() => undefined);
    else local.setHistory([]);
  }, [signedIn]);

  const isFavourite = useCallback((e: DecisionEntry) => favourites.some((f) => sameKey(f, e)), [favourites]);

  const removeFavourite = useCallback((id: string) => {
    setFavourites((prev) => {
      const next = prev.filter((f) => f.id !== id);
      if (!signedIn) local.setFavourites(next);
      return next;
    });
    if (signedIn) api.delete(`/me/favourites/${id}`).catch(() => undefined);
  }, [signedIn]);

  const toggleFavourite = useCallback((e: DecisionEntry) => {
    const existing = favourites.find((f) => sameKey(f, e));
    if (existing) {
      removeFavourite(existing.id);
      return false;
    }
    const item = newItem(e);
    setFavourites((prev) => {
      const next = [item, ...prev];
      if (!signedIn) local.setFavourites(next);
      return next;
    });
    if (signedIn) api.post("/me/favourites", toIn(item)).catch(() => undefined);
    return true;
  }, [favourites, removeFavourite, signedIn]);

  const setPrefs = useCallback((p: Partial<Prefs>) => {
    setPrefsState((prev) => {
      const next = { ...prev, ...p };
      local.setPrefs(next);
      if (signedIn) api.put("/me/preferences", next).catch(() => undefined);
      return next;
    });
  }, [signedIn]);

  const recordDislike = useCallback((label: string) => {
    setPrefs({ dislikes: [...prefs.dislikes.filter((d) => d !== label), label].slice(-200) });
  }, [prefs.dislikes, setPrefs]);

  const saveList = useCallback((l: WheelList) => {
    const item = { ...l, updated_at: new Date().toISOString() };
    setLists((prev) => {
      const next = [item, ...prev.filter((x) => x.id !== l.id)];
      if (!signedIn) local.setLists(next);
      return next;
    });
    if (signedIn) api.put(`/me/lists/${l.id}`, listIn(item)).catch(() => undefined);
  }, [signedIn]);

  const deleteList = useCallback((id: string) => {
    setLists((prev) => {
      const next = prev.filter((x) => x.id !== id);
      if (!signedIn) local.setLists(next);
      return next;
    });
    if (signedIn) api.delete(`/me/lists/${id}`).catch(() => undefined);
  }, [signedIn]);

  useEffect(() => {
    if (!prefs.sound_effects) return;
    const unlock = () => sfx.unlock();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, [prefs.sound_effects]);

  const fastMode = prefs.reduced_motion || systemPrefersReducedMotion() || !!isLowPowerDevice();

  const value = useMemo(
    () => ({ history, favourites, prefs, fastMode, signedIn, addHistory, clearHistory, isFavourite, toggleFavourite, removeFavourite, setPrefs, recordDislike, lists, saveList, deleteList }),
    [history, favourites, prefs, fastMode, signedIn, addHistory, clearHistory, isFavourite, toggleFavourite, removeFavourite, setPrefs, recordDislike, lists, saveList, deleteList],
  );
  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

export const useData = () => useContext(DataCtx);
